import { productForHost, tenantOverrideForLocation, tenantWorkspaceUrl } from '../config/productBoundary';
// =============================================================================
// TENANT CONTEXT
// Provides multi-tenant state throughout the app
// =============================================================================

import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { supabase, clearTenantIdCache } from '../lib/supabase';
import { INDUSTRY_CONFIGS, IndustryConfig, IndustryTerminology, DEFAULT_TERMINOLOGY } from '../config/industries';
import { resolveTheme, applyThemeToDocument, applyRegionColorsToDocument, isBackOfficeSurface, getThemeForHost, bootstrapHostTheme, TenantTheme, ThemePresetId, RegionColors } from '../config/themes';
import { tenantSlugForHost, brandingFallbackForSlug, resolveThemeForTenant } from '../config/hostTenants';
import { logger } from '../utils/logger';

const log = logger.module('TenantContext');

export interface TenantSettings {
  custom_dimensions?: Array<{ value: string; label: string }>;
  // Master Control Center launchpad — external URLs to deep-link from
  // the Home tab into the tenant's dedicated Sales / Operations sub-apps.
  // Stored on the existing tenants.settings JSONB column; null/undefined
  // renders the tile in a "coming soon" state.
  salesUrl?: string;
  opsUrl?: string;
  /** Optional sales director / manager for executive drilldown (email + phone for owner reach-out). */
  salesSupervisor?: { name?: string; email?: string; phone?: string };
  /** Display roster for sales coverage panel; defaults to flooring built-ins when missing. */
  salesTeam?: string[];
  // -------------------------------------------------------------------------
  // Map settings (Renovation Flooring jobsite map)
  // -------------------------------------------------------------------------
  /** Which map library to render in the Flooring executive view.
   *  'leaflet' — CartoDB dark (default, no API key needed)
   *  'google'  — Google Maps JS API (requires VITE_GOOGLE_MAPS_API_KEY) */
  mapProvider?: 'leaflet' | 'google';
  /** Google Maps color preset id (see mapStylePresets.ts). Defaults to 'beige_emerald'. */
  googleMapStyleId?: string;
  /** Per-region background color overrides (Elementor-style zone tinting).
   *  Each key maps to a --color-region-* CSS custom property. */
  regionColors?: RegionColors;
  /** Realtimax Page Builder saved layout for this tenant (one per tenant).
   *  Stored as a JSON tree of zones / shapes / blocks / tools. Loaded by
   *  PageBuilderContext at mount; persisted via updateSettings(). The shape
   *  is intentionally typed as `unknown` here so this file does not depend
   *  on the page builder module — the builder narrows it at use site. */
  pageBuilderLayout?: unknown;
  /** Bliss Basic landing price box (basic.blissbunkbeds.com). */
  blissBasicLanding?: {
    startingPricePrefix?: string;
    startingPriceUsd?: number;
    investmentNote?: string;
  };
  /** Partner uses /basic-admin catalog studio (e.g. Beach Bumz). */
  blissBasicPartner?: boolean;
  /** Catalog owner display name (e.g. Josh Steintz for Beach Bumz). */
  owner_name?: string;
  [key: string]: unknown;
}

export interface TenantOption {
  id: string;
  name: string;
  slug: string;
  industry: string;
  role: string;
  primaryColor: string | null;
  logoUrl: string | null;
}

interface TenantContextType {
  tenantId: string | null;
  tenantName: string | null;
  tenantSlug: string | null;
  industry: IndustryConfig | null;
  plan: string | null;
  role: string | null;
  primaryColor: string | null;
  themePreset: string | null;
  logoUrl: string | null;
  settings: TenantSettings;
  isLoading: boolean;
  error: string | null;
  /** All tenants visible to the current user. For platform admins this is
   *  every active tenant in the system; for everyone else it is just the
   *  tenants they have a `tenant_members` row in. */
  availableTenants: TenantOption[];
  /** True iff the signed-in user has profiles.is_platform_admin = TRUE. */
  isPlatformAdmin: boolean;
  /** True iff the current active tenant is one the user is impersonating
   *  (platform admin viewing a tenant they have no membership in). */
  isImpersonating: boolean;
  /** Effective explicit workspace selection (URL-local on registered product hosts). */
  activeTenantPinnedId: string | null;
  refetch: () => Promise<void>;
  updateSettings: (patch: Partial<TenantSettings>) => Promise<void>;
  /** Pin the active tenant locally + reload context. Persists across sessions. */
  switchTenant: (tenantId: string) => Promise<void>;
  /** End the current impersonation session and clear active_tenant_id so the
   *  next loadTenant falls back to the user's first real membership. */
  exitImpersonation: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
  tenantId: null,
  tenantName: null,
  tenantSlug: null,
  industry: null,
  plan: null,
  role: null,
  primaryColor: null,
  themePreset: null,
  logoUrl: null,
  settings: {},
  isLoading: true,
  error: null,
  availableTenants: [],
  isPlatformAdmin: false,
  isImpersonating: false,
  activeTenantPinnedId: null,
  refetch: async () => {},
  updateSettings: async () => {},
  switchTenant: async () => {},
  exitImpersonation: async () => {},
});

export function TenantProvider({ children }: { children: ReactNode }) {
  const loadVersion = useRef(0);
  const [state, setState] = useState<TenantContextType>({
    tenantId: null,
    tenantName: null,
    tenantSlug: null,
    industry: null,
    plan: null,
    role: null,
    primaryColor: null,
    themePreset: null,
    logoUrl: null,
    settings: {},
    isLoading: true,
    error: null,
    availableTenants: [],
    isPlatformAdmin: false,
    isImpersonating: false,
    activeTenantPinnedId: null,
    refetch: async () => {},
    updateSettings: async () => {},
    switchTenant: async () => {},
    exitImpersonation: async () => {},
  });

  const switchTenant = useCallback(async (tenantId: string) => {
    clearTenantIdCache();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Known product hosts use explicit, URL-local workspace selection.
    // Legacy/unmapped installations retain their existing profile preference.
    if (typeof window !== 'undefined' && productForHost(window.location.hostname)) {
      if (window.location.pathname !== '/platform' && window.location.pathname !== '/admin' && !window.location.pathname.startsWith('/admin/')) throw new Error('Open tenant administration to select a different business workspace.');
      window.history.replaceState(window.history.state, '', tenantWorkspaceUrl(window.location.pathname, window.location.search, tenantId));
    } else {
      const { error } = await supabase.from('profiles').update({ active_tenant_id: tenantId }).eq('id', user.id);
      if (error) throw error;
    }

    // Audit-log impersonation: a platform admin entering a tenant they are
    // NOT a member of. The unique partial index `idx_one_open_session_per_user`
    // guarantees at most one open row per user, so close any prior open row
    // first by setting ended_at. The is_platform_admin RLS gate handles the
    // permission check on insert.
    const { data: profileRow } = await supabase
      .from('profiles')
      .select('is_platform_admin')
      .eq('id', user.id)
      .maybeSingle();
    const isAdmin = !!(profileRow as { is_platform_admin?: boolean } | null)?.is_platform_admin;

    if (isAdmin) {
      const { data: membershipRow } = await supabase
        .from('tenant_members')
        .select('tenant_id')
        .eq('user_id', user.id)
        .eq('tenant_id', tenantId)
        .maybeSingle();
      const isMember = !!membershipRow;

      // Close any open prior session regardless — switching tenants ends the
      // previous impersonation, even if the new one is a real membership.
      await supabase
        .from('platform_admin_impersonation_log')
        .update({ ended_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .is('ended_at', null);

      if (!isMember) {
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null;
        const { error: logError } = await supabase
          .from('platform_admin_impersonation_log')
          .insert({
            user_id: user.id,
            tenant_id: tenantId,
            started_at: new Date().toISOString(),
            device_user_agent: ua,
          });
        if (logError) {
          log.error('switchTenant: failed to write impersonation log', logError);
        }
      }
    }

    await loadTenant();
  }, []);

  const exitImpersonation = useCallback(async () => {
    clearTenantIdCache();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Close any open impersonation session.
    await supabase
      .from('platform_admin_impersonation_log')
      .update({ ended_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .is('ended_at', null);

    if (typeof window !== 'undefined' && productForHost(window.location.hostname)) {
      const url = new URL(window.location.href);
      url.searchParams.delete('tenant'); url.searchParams.delete('workspace');
      window.history.replaceState(window.history.state, '', url.pathname + url.search);
    } else {
      const { error } = await supabase.from('profiles').update({ active_tenant_id: null }).eq('id', user.id);
      if (error) throw error;
    }

    await loadTenant();
  }, []);

  const updateSettings = useCallback(async (patch: Partial<TenantSettings>) => {
    let tenantId: string | null = null;
    let merged: TenantSettings = {};

    setState((prev) => {
      tenantId = prev.tenantId;
      if (!prev.tenantId) return prev;
      merged = { ...prev.settings, ...patch };
      return { ...prev, settings: merged };
    });

    if (!tenantId) {
      throw new Error('No active tenant');
    }

    const { error } = await supabase
      .from('tenants' as any)
      .update({ settings: merged } as any)
      .eq('id', tenantId);

    if (error) {
      log.error('Failed to save tenant settings: ' + error.message);
      throw error;
    }
    log.debug('Tenant settings saved');
  }, []);

  // Apply the host's brand theme. Used on every code path where we cannot
  // resolve a tenant (signed out, no membership, query error, exception) so
  // that the static :root fallback in buttons.css is always overridden by an
  // intentional, documented theme — never left to whatever happened to be
  // applied previously. The host (realtimax.com vs blissbunkbeds.com vs
  // anything else) decides which preset; see getThemeForHost in themes.ts.
  const applyDefaultTheme = () => {
    const hostPreset = typeof window !== 'undefined'
      ? getThemeForHost(window.location.hostname)
      : null;
    applyThemeToDocument(resolveTheme(hostPreset));
    applyRegionColorsToDocument(null);
  };

  const loadPublicTenantByHost = async (isCurrent = () => true): Promise<boolean> => {
    const hostSlug =
      typeof window !== 'undefined' ? tenantSlugForHost(window.location.hostname) : null;
    if (!hostSlug) return false;

    const { data: tenant, error } = await supabase
      .from('tenants')
      .select('id, name, slug, industry, plan, primary_color, theme_preset, logo_url, settings')
      .eq('slug', hostSlug)
      .eq('is_active', true)
      .maybeSingle();

    if (!isCurrent()) return true;
    if (error || !tenant) {
      log.debug('No public tenant for host slug: ' + hostSlug);
      if (typeof window !== 'undefined' && window.location.hostname.includes('blissbunkbeds')) {
        const fallback = brandingFallbackForSlug('bliss-bedrooms');
        if (window.location.pathname !== '/login') {
          applyThemeToDocument(resolveThemeForTenant('bliss-bedrooms', 'classic-gold', null));
        }
        setState({
          tenantId: null,
          tenantName: 'Bliss Bedrooms LLC',
          tenantSlug: 'bliss-bedrooms',
          industry: null,
          plan: null,
          role: null,
          primaryColor: null,
          themePreset: 'classic-gold',
          logoUrl: fallback.logoUrl || null,
          settings: {},
          isLoading: false,
          error: null,
          availableTenants: [],
          isPlatformAdmin: false,
          isImpersonating: false,
          activeTenantPinnedId: null,
          refetch: loadTenant,
          updateSettings,
          switchTenant,
          exitImpersonation,
        });
        return true;
      }
      return false;
    }

    const industryKey = tenant.industry as keyof typeof INDUSTRY_CONFIGS;
    const industryConfig = INDUSTRY_CONFIGS[industryKey] || null;
    const tenantSettings: TenantSettings = (tenant as any).settings || {};
    const fallback = brandingFallbackForSlug(tenant.slug);
    const theme = resolveThemeForTenant(tenant.slug, tenant.theme_preset, tenant.primary_color);

    if (window.location.pathname !== '/login') {
      applyThemeToDocument(theme);
      applyRegionColorsToDocument(
        isBackOfficeSurface(window.location.pathname) ? null : tenantSettings.regionColors,
      );
    }

    setState({
      tenantId: tenant.id,
      tenantName: tenant.name,
      tenantSlug: tenant.slug,
      industry: industryConfig,
      plan: tenant.plan,
      role: null,
      primaryColor: tenant.primary_color,
      themePreset: tenant.theme_preset,
      logoUrl: tenant.logo_url || fallback.logoUrl || null,
      settings: tenantSettings,
      isLoading: false,
      error: null,
      availableTenants: [],
      isPlatformAdmin: false,
      isImpersonating: false,
      activeTenantPinnedId: null,
      refetch: loadTenant,
      updateSettings,
      switchTenant,
      exitImpersonation,
    });
    return true;
  };

  const loadTenant = async () => {
    const version = ++loadVersion.current;
    const isCurrent = () => version === loadVersion.current;
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!isCurrent()) return;
      if (!user) {
        log.debug('No authenticated user — resolving public tenant by host');
        const loaded = await loadPublicTenantByHost(isCurrent);
        if (!isCurrent()) return;
        if (!loaded) {
          applyDefaultTheme();
          setState(s => ({ ...s, isLoading: false }));
        }
        return;
      }

      // Pull every membership the user has so the super-admin switcher has
      // the full list to render and so we can resolve the active tenant by
      // priority: localStorage override → host map → first membership.
      const { data: allMemberships, error: membershipError } = await supabase
        .from('tenant_members')
        .select('tenant_id, role, tenants:tenant_id(id, name, slug, industry, primary_color, logo_url)')
        .eq('user_id', user.id);

      if (!isCurrent()) return;
      if (membershipError) {
        log.error('Error fetching memberships: ' + membershipError.message);
        applyDefaultTheme();
        setState(s => ({ ...s, isLoading: false, error: membershipError.message }));
        return;
      }

      const rows = (allMemberships ?? []) as Array<{
        tenant_id: string;
        role: string;
        tenants: {
          id: string;
          name: string;
          slug: string;
          industry: string;
          primary_color: string | null;
          logo_url: string | null;
        } | null;
      }>;

      const availableTenants: TenantOption[] = rows
        .filter((r) => !!r.tenants)
        .map((r) => ({
          id: r.tenants!.id,
          name: r.tenants!.name,
          slug: r.tenants!.slug,
          industry: r.tenants!.industry,
          role: r.role,
          primaryColor: r.tenants!.primary_color,
          logoUrl: r.tenants!.logo_url,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      // 1) profiles.active_tenant_id + is_platform_admin
      const { data: profile } = await supabase
        .from('profiles')
        .select('active_tenant_id, is_platform_admin')
        .eq('id', user.id)
        .maybeSingle();
      if (!isCurrent()) return;
      const legacyId = (profile as { active_tenant_id: string | null } | null)?.active_tenant_id ?? null;
      const overrideId = typeof window !== 'undefined' ? tenantOverrideForLocation(window.location, legacyId) : legacyId;
      const hostSlug = typeof window !== 'undefined' ? tenantSlugForHost(window.location.hostname) : null;
      const isPlatformAdmin = !!(profile as { is_platform_admin?: boolean } | null)?.is_platform_admin;

      // Platform admins see every active tenant in the dropdown, not just
      // the ones they have a tenant_members row in. RLS overlay
      // `tenants_platform_admin_select` (20260515) authorizes the read.
      let availableForAdmin: TenantOption[] = availableTenants;
      if (isPlatformAdmin) {
        const memberIds = new Set(rows.map((r) => r.tenant_id));
        const { data: allTenants, error: allTenantsErr } = await (supabase as any)
          .from('tenants')
          .select('id, name, slug, industry, primary_color, logo_url')
          .eq('is_active', true)
          .order('name', { ascending: true });
        if (!isCurrent()) return;
        if (allTenantsErr) {
          log.warn('Platform admin: failed to fetch all tenants — falling back to memberships only: ' + allTenantsErr.message);
        } else if (allTenants) {
          availableForAdmin = (allTenants as Array<{
            id: string; name: string; slug: string; industry: string;
            primary_color: string | null; logo_url: string | null;
          }>).map((t) => {
            const isMember = memberIds.has(t.id);
            const memberRow = isMember ? rows.find((r) => r.tenant_id === t.id) : null;
            return {
              id: t.id,
              name: t.name,
              slug: t.slug,
              industry: t.industry,
              role: memberRow?.role ?? 'platform_admin',
              primaryColor: t.primary_color,
              logoUrl: t.logo_url,
            };
          });
        }
      }

      let chosen = overrideId ? rows.find((r) => r.tenant_id === overrideId) : null;

      // Platform admin: allow selecting a tenant they have no membership
      // row in. We synthesize a virtual membership so the rest of the flow
      // (which keys off `chosen.tenant_id` + `chosen.role`) keeps working.
      if (!chosen && isPlatformAdmin && overrideId) {
        const t = availableForAdmin.find((x) => x.id === overrideId);
        if (t) {
          chosen = {
            tenant_id: t.id,
            role: 'platform_admin',
            tenants: {
              id: t.id, name: t.name, slug: t.slug, industry: t.industry,
              primary_color: t.primaryColor, logo_url: t.logoUrl,
            },
          };
        }
      }

      if (overrideId && !chosen && hostSlug) {
        setState(prev => ({ ...prev, tenantId: null, tenantSlug: null, tenantName: null, settings: {}, industry: null, role: null, isLoading: false, error: 'Selected workspace is unavailable or not authorized.' }));
        return;
      }

      // 2) host-based mapping
      if (!chosen) {
        if (hostSlug) {
          chosen = rows.find((r) => r.tenants?.slug === hostSlug) ?? null;
          if (!chosen && isPlatformAdmin) {
            const t = availableForAdmin.find(t => t.slug === hostSlug);
            if (t) chosen = { tenant_id: t.id, role: 'platform_admin', tenants: { id:t.id, name:t.name, slug:t.slug, industry:t.industry, primary_color:t.primaryColor, logo_url:t.logoUrl } };
          }
        }
      }

      // 3) first membership (or first visible tenant for platform admins)
      if (!chosen && !hostSlug) {
        chosen = rows[0] ?? null;
        if (!chosen && isPlatformAdmin && availableForAdmin.length > 0) {
          const first = availableForAdmin[0];
          chosen = {
            tenant_id: first.id,
            role: 'platform_admin',
            tenants: {
              id: first.id, name: first.name, slug: first.slug, industry: first.industry,
              primary_color: first.primaryColor, logo_url: first.logoUrl,
            },
          };
        }
      }

      if (!chosen && hostSlug) {
        setState(prev => ({ ...prev, tenantId: null, tenantSlug: null, tenantName: null, settings: {}, industry: null, role: null, isLoading: false, error: 'No authorized business is configured for this product domain.' }));
        return;
      }

      const membership = chosen
        ? { tenant_id: chosen.tenant_id, role: chosen.role }
        : null;

      if (membership) {
        // Fetch tenant details separately to avoid join type issues
        const { data: tenant, error: tenantError } = await supabase
          .from('tenants')
          .select('name, slug, industry, plan, primary_color, theme_preset, logo_url, settings')
          .eq('id', membership.tenant_id)
          .single();

        if (!isCurrent()) return;
        if (tenantError || !tenant) {
          log.error('Error fetching tenant details: ' + (tenantError?.message || 'not found'));
          applyDefaultTheme();
          setState(s => ({ ...s, isLoading: false, error: tenantError?.message || 'Tenant not found' }));
          return;
        }

        const industryKey = tenant.industry as keyof typeof INDUSTRY_CONFIGS;
        const industryConfig = INDUSTRY_CONFIGS[industryKey] || null;

        log.debug('Tenant loaded: ' + tenant.name + ' | Industry: ' + tenant.industry + ' | Role: ' + membership.role);

        // Apply tenant color scheme to CSS custom properties.
        // Skip on the login route — Login.tsx locks the host brand theme via
        // useLayoutEffect and we must not overwrite it with the last tenant's
        // colours (which would cause the wrong brand flash on realtimax.com).
        const theme = resolveThemeForTenant(tenant.slug, tenant.theme_preset, tenant.primary_color);
        const tenantSettings: TenantSettings = (tenant as any).settings || {};
        const path = window.location.pathname;
        if (path !== '/login') {
          // Back-office routes (including the /basic-admin studio) are
          // re-surfaced onto pearl inside applyThemeToDocument, and drop the
          // tenant's per-region background overrides so staff tools match.
          applyThemeToDocument(theme);
          applyRegionColorsToDocument(
            isBackOfficeSurface(path) ? null : tenantSettings.regionColors,
          );
        }
        log.debug('Theme applied: ' + (tenant.theme_preset || 'default (realtimax)'));

        const isImpersonating = isPlatformAdmin
          && !rows.some((r) => r.tenant_id === membership.tenant_id);

        setState({
          tenantId: membership.tenant_id,
          tenantName: tenant.name,
          tenantSlug: tenant.slug,
          industry: industryConfig,
          plan: tenant.plan,
          role: membership.role,
          primaryColor: tenant.primary_color,
          themePreset: tenant.theme_preset,
          logoUrl: tenant.logo_url || brandingFallbackForSlug(tenant.slug).logoUrl || null,
          settings: tenantSettings,
          isLoading: false,
          error: null,
          availableTenants: availableForAdmin,
          isPlatformAdmin,
          isImpersonating,
          activeTenantPinnedId: overrideId,
          refetch: loadTenant,
          updateSettings,
          switchTenant,
          exitImpersonation,
        });
      } else {
        log.debug('User has no tenant membership — applying default theme');
        applyDefaultTheme();
        setState(s => ({
          ...s,
          isLoading: false,
          availableTenants: availableForAdmin,
          isPlatformAdmin,
          isImpersonating: false,
          activeTenantPinnedId: overrideId,
          refetch: loadTenant,
          switchTenant,
          exitImpersonation,
        }));
      }
    } catch (err: any) {
      if (!isCurrent()) return;
      log.error('Tenant load error:', err?.message || err);
      applyDefaultTheme();
      setState(s => ({ ...s, isLoading: false, error: err?.message, refetch: loadTenant }));
    }
  };

  useEffect(() => {
    bootstrapHostTheme();
    loadTenant();

    let reloadTimer: ReturnType<typeof setTimeout> | undefined;
    // Leave the auth callback before making another authenticated request.
    // Re-load tenant when auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        ++loadVersion.current;
        clearTimeout(reloadTimer);
        setState(s => ({ ...s, isLoading: true }));
        reloadTimer = setTimeout(() => { void loadTenant(); }, 0);
      } else if (event === 'SIGNED_OUT') {
        ++loadVersion.current;
        clearTimeout(reloadTimer);
        setState({
          tenantId: null,
          tenantName: null,
          tenantSlug: null,
          industry: null,
          plan: null,
          role: null,
          primaryColor: null,
          themePreset: null,
          logoUrl: null,
          settings: {},
          isLoading: false,
          error: null,
          availableTenants: [],
          isPlatformAdmin: false,
          isImpersonating: false,
          activeTenantPinnedId: null,
          refetch: loadTenant,
          updateSettings,
          switchTenant,
          exitImpersonation,
        });
        // Reset to umbrella default theme on sign out (NOT classic-gold,
        // which is the Bliss tenant's theme — that would leak Bliss colors
        // onto every other tenant after they log out).
        applyDefaultTheme();
      }
    });

    return () => {
      ++loadVersion.current;
      clearTimeout(reloadTimer);
      subscription?.unsubscribe();
    };
  }, []);

  return <TenantContext.Provider value={state}>{children}</TenantContext.Provider>;
}

// Core hook — full tenant context
export function useTenant(): TenantContextType {
  return useContext(TenantContext);
}

// Convenience hook — industry-aware terminology
export function useTerms(): IndustryTerminology {
  const { industry } = useTenant();
  return industry?.terminology ?? DEFAULT_TERMINOLOGY;
}

// Convenience hook — feature flags
export function useFeatures(): IndustryConfig['features'] | null {
  const { industry } = useTenant();
  return industry?.features ?? null;
}

// Convenience hook — job statuses for current industry
export function useJobStatuses(): string[] {
  const { industry } = useTenant();
  return industry?.jobStatuses ?? ['New', 'In Progress', 'Completed'];
}

// Convenience hook — resolved theme object
export function useTheme(): TenantTheme {
  const { tenantSlug, themePreset, primaryColor } = useTenant();
  return resolveThemeForTenant(tenantSlug, themePreset, primaryColor);
}

export default TenantContext;


